/**
 * Fifty Rooms <fr-results> — collection and search results (snippets/fr-collection-results.liquid).
 * Filters / sort / chips → Section Rendering API (?section_id=…) + pushState; Load more / Load previous;
 * Hide/Show filters (4 ↔ 5 columns, sessionStorage); mobile drawer (focus trap, Esc, scroll lock);
 * design search + Show more; promo tile placement (grid-row, collision + short-collection handling).
 *
 * Events (bubble from <fr-results>):
 *   fr:results:loading   { url }
 *   fr:results:updated   { url, count, activeCount }
 *   fr:results:loaded-more { page, added, direction: 'next' | 'previous' }
 *   fr:results:error     { url, error, kind: 'update' | 'load-more' }
 *   fr:filters:toggle    { hidden }
 *   fr:drawer:open       { mode: 'filters' | 'sort' }
 *   fr:drawer:close      {}
 * Shopify standard events (CollectionUpdateEvent / SearchUpdateEvent) are dispatched when available.
 */

const DRAWER_MQ = '(max-width: 989px)';
const STORAGE_KEY = 'fr-filters-hidden';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

function safeSession(fn) {
  try {
    return fn(window.sessionStorage);
  } catch (e) {
    return null;
  }
}

class FrResults extends HTMLElement {
  #controller = null;
  #lastFocus = null;
  #ro = null;
  #promoFrame = 0;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('popstate', this.#onPopState);

    if (!this.hasAttribute('data-filters-hidden')) {
      const stored = safeSession((s) => s.getItem(STORAGE_KEY));
      if (stored === '1' || (stored === null && this.dataset.filtersDefault === 'hidden')) {
        this.setAttribute('data-filters-hidden', '');
      }
    }
    this.#syncToggle();
    this.#observeGrid();
    this.layoutPromos();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPopState);
    this.#ro?.disconnect();
    document.documentElement.classList.remove('fr-scroll-lock');
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get filters() {
    return this.querySelector('[data-fr-filters]');
  }

  get filterForm() {
    return this.querySelector('[data-fr-filter-form]');
  }

  get grid() {
    return this.querySelector('[data-fr-grid]');
  }

  get isDrawerMode() {
    return window.matchMedia(DRAWER_MQ).matches;
  }

  #emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  /* ---------------- events ---------------- */

  #onChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLElement)) return;

    if (t.matches('[data-fr-sort]')) {
      this.#setSort(t.value);
      return;
    }
    if (t.matches('[data-fr-sort-radio]')) {
      this.#setSort(t.value);
      return;
    }
    if (t.matches('[data-fr-filter-input], [data-fr-price-input]')) {
      this.update(this.#urlFromForm());
    }
  };

  #onInput = (event) => {
    const t = event.target;
    if (t instanceof HTMLInputElement && t.matches('[data-fr-design-search]')) {
      this.#applyDesignSearch(t.closest('.fr-filter__content'));
    }
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (form.matches('[data-fr-filter-form]')) this.update(this.#urlFromForm());
    else this.update(this.#urlFromForm(form));
  };

  #onClick = (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;

    const toggle = t.closest('[data-fr-filter-toggle]');
    if (toggle) return this.toggleFilters();

    const opener = t.closest('[data-fr-drawer-open]');
    if (opener) return this.openDrawer(opener.getAttribute('data-fr-drawer-open') || 'filters', opener);

    if (t.closest('[data-fr-drawer-close]')) return this.closeDrawer();

    if (t.closest('[data-fr-drawer-apply]')) {
      this.closeDrawer();
      this.#scrollToResults();
      return;
    }

    const sortFocus = t.closest('[data-fr-sort-focus]');
    if (sortFocus) {
      this.querySelector('[data-fr-sort]')?.focus();
      return;
    }

    const more = t.closest('[data-fr-show-more]');
    if (more) return this.#toggleShowMore(more);

    const link = t.closest('a[data-fr-chip], a[data-fr-clear-all]');
    if (link instanceof HTMLAnchorElement) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.update(link.href);
      return;
    }

    const loadMore = t.closest('a[data-fr-load-more]');
    if (loadMore instanceof HTMLAnchorElement) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.loadMore(loadMore.href, 'next', event.detail === 0);
      return;
    }

    const loadPrev = t.closest('a[data-fr-load-previous]');
    if (loadPrev instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.loadMore(loadPrev.href, 'previous', event.detail === 0);
      return;
    }

    const retry = t.closest('[data-fr-retry]');
    if (retry) {
      const href = retry.getAttribute('data-href') || this.querySelector('a[data-fr-load-more]')?.href;
      if (href) this.loadMore(href, 'next', event.detail === 0);
    }
  };

  #onKeydown = (event) => {
    const drawer = this.filters;
    if (!drawer || !drawer.hasAttribute('data-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #onPopState = () => {
    const url = new URL(window.location.href);
    if (url.pathname !== new URL(this.#formAction(), window.location.origin).pathname) return;
    url.searchParams.delete('page');
    this.update(url.toString(), { push: false });
  };

  /* ---------------- URL helpers ---------------- */

  #formAction(form = this.filterForm || this.querySelector('[data-fr-sort-form]')) {
    return form?.getAttribute('action') || window.location.pathname;
  }

  #urlFromForm(form = this.filterForm || this.querySelector('[data-fr-sort-form]')) {
    const url = new URL(this.#formAction(form), window.location.origin);
    const params = new URLSearchParams();
    if (form) {
      for (const [key, value] of new FormData(form).entries()) {
        if (key.startsWith('fr_')) continue;
        if (typeof value !== 'string' || value === '') continue;
        params.append(key, value);
      }
    }
    url.search = params.toString();
    return url.toString();
  }

  #setSort(value) {
    this.querySelectorAll('[data-fr-sort-input]').forEach((i) => (i.value = value));
    this.querySelectorAll('[data-fr-sort]').forEach((s) => (s.value = value));
    this.querySelectorAll('[data-fr-sort-radio]').forEach((r) => (r.checked = r.value === value));
    const form = this.filterForm;
    if (form) {
      this.update(this.#urlFromForm(form));
    } else {
      this.update(this.#urlFromForm(this.querySelector('[data-fr-sort-form]')));
    }
  }

  /* ---------------- update (filters / sort) ---------------- */

  async update(url, { push = true } = {}) {
    const target = new URL(url, window.location.origin);
    target.searchParams.delete('page');
    target.searchParams.delete('section_id');

    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    this.#controller?.abort();
    this.#controller = new AbortController();
    this.setAttribute('data-loading', '');
    this.setAttribute('aria-busy', 'true');
    this.#emit('fr:results:loading', { url: target.toString() });

    const state = this.#captureState();
    let renderResolve;
    const renderPromise = new Promise((resolve) => (renderResolve = resolve));
    this.#dispatchStandardEvent(renderPromise);

    try {
      const response = await fetch(fetchUrl, { signal: this.#controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('fr-results');
      if (!next) throw new Error('fr-results missing in response');

      if (push) history.pushState({ frResults: true }, '', target.toString());
      this.#swapRegions(next);
      this.#restoreState(state);
      this.dataset.resultsCount = next.dataset.resultsCount || '0';
      this.dataset.activeCount = next.dataset.activeCount || '0';
      this.#observeGrid();
      this.layoutPromos();
      renderResolve(Number(this.dataset.resultsCount));
      this.#emit('fr:results:updated', {
        url: target.toString(),
        count: Number(this.dataset.resultsCount),
        activeCount: Number(this.dataset.activeCount),
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      renderResolve(Number(this.dataset.resultsCount));
      this.#emit('fr:results:error', { url: target.toString(), error: String(error), kind: 'update' });
      window.location.assign(target.toString());
    } finally {
      this.removeAttribute('data-loading');
      this.removeAttribute('aria-busy');
    }
  }

  #swapRegions(next) {
    for (const region of this.querySelectorAll('[data-fr-region]')) {
      const name = region.getAttribute('data-fr-region');
      const incoming = next.querySelector(`[data-fr-region="${name}"]`);
      if (incoming) region.innerHTML = incoming.innerHTML;
    }
  }

  #captureState() {
    const open = {};
    this.querySelectorAll('details[data-fr-filter-group]').forEach((d) => (open[d.id] = d.open));
    const search = {};
    this.querySelectorAll('[data-fr-design-search]').forEach((i) => (search[i.id] = i.value));
    const expanded = [...this.querySelectorAll('[data-fr-show-more][aria-expanded="true"]')].map(
      (b) => b.closest('details')?.id
    );
    const active = document.activeElement;
    return {
      open,
      search,
      expanded,
      focusId: active && this.contains(active) ? active.id : '',
      bodyScroll: this.querySelector('.fr-filters__body')?.scrollTop || 0,
    };
  }

  #restoreState(state) {
    this.querySelectorAll('details[data-fr-filter-group]').forEach((d) => {
      if (d.id in state.open) d.open = state.open[d.id];
    });
    for (const [id, value] of Object.entries(state.search)) {
      const input = id && this.querySelector(`#${CSS.escape(id)}`);
      if (input instanceof HTMLInputElement) {
        input.value = value;
        this.#applyDesignSearch(input.closest('.fr-filter__content'));
      }
    }
    for (const id of state.expanded) {
      const btn = id && this.querySelector(`#${CSS.escape(id)} [data-fr-show-more]`);
      if (btn) this.#toggleShowMore(btn, true);
    }
    const body = this.querySelector('.fr-filters__body');
    if (body) body.scrollTop = state.bodyScroll;
    if (state.focusId) {
      const el = this.querySelector(`#${CSS.escape(state.focusId)}`);
      if (el instanceof HTMLElement) el.focus({ preventScroll: true });
    }
  }

  async #dispatchStandardEvent(renderPromise) {
    try {
      const events = await import('@shopify/events');
      const isSearch = this.dataset.pageType === 'search';
      const EventClass = isSearch ? events.SearchUpdateEvent : events.CollectionUpdateEvent;
      if (!EventClass) return;
      const deferred = EventClass.createPromise();
      const productFilters = EventClass.parseProductFilters();
      let event;
      if (isSearch) {
        event = new EventClass({
          search: {
            query: new URL(window.location.href).searchParams.get('q') || '',
            ...(productFilters && { productFilters }),
            sortKey: EventClass.getSortKey(),
          },
          promise: deferred.promise,
        });
        renderPromise.then((count) => deferred.resolve({ totalCount: count }));
      } else {
        const rawId = this.dataset.collectionId;
        event = new EventClass({
          collection: {
            id: rawId && /^\d+$/.test(rawId) ? rawId : null,
            handle: this.dataset.collectionHandle,
            productsCount: Number(this.dataset.resultsCount) || 0,
          },
          ...(productFilters && { productFilters }),
          sortKey: EventClass.getSortKey(),
          promise: deferred.promise,
        });
        renderPromise.then((count) => deferred.resolve({ productsCount: count }));
      }
      this.dispatchEvent(event);
    } catch (e) {
      /* analytics is optional */
    }
  }

  /* ---------------- load more ---------------- */

  async loadMore(href, direction = 'next', keyboard = false) {
    const grid = this.grid;
    if (!grid) return;
    const wrap = this.querySelector('[data-fr-load-more-wrap]');
    const button = direction === 'next' ? this.querySelector('a[data-fr-load-more]') : this.querySelector('a[data-fr-load-previous]');
    const errorEl = this.querySelector('[data-fr-load-error]');
    button?.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.hidden = true;

    const pageUrl = new URL(href, window.location.origin);
    pageUrl.searchParams.delete('section_id');
    const fetchUrl = new URL(pageUrl);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.querySelector('[data-fr-grid]');
      if (!nextGrid) throw new Error('grid missing in response');
      const cards = [...nextGrid.querySelectorAll(':scope > [data-fr-card]')];
      const existing = new Set([...grid.querySelectorAll(':scope > [data-fr-card]')].map((c) => c.id));
      const fresh = cards.filter((c) => !existing.has(c.id));
      const imported = fresh.map((c) => document.importNode(c, true));
      const page = Number(nextGrid.dataset.page) || 0;

      if (direction === 'next') {
        const lastCard = [...grid.querySelectorAll(':scope > [data-fr-card]')].pop();
        if (lastCard) lastCard.after(...imported);
        else grid.prepend(...imported);
        const nextWrap = doc.querySelector('[data-fr-load-more-wrap]');
        if (wrap && nextWrap) {
          wrap.innerHTML = nextWrap.innerHTML;
          const retry = wrap.querySelector('[data-fr-retry]');
          retry?.removeAttribute('data-href');
        }
        history.replaceState(history.state, '', pageUrl.toString());
      } else {
        const before = grid.getBoundingClientRect().height;
        const scrollY = window.scrollY;
        grid.prepend(...imported);
        const delta = grid.getBoundingClientRect().height - before;
        window.scrollTo({ top: scrollY + delta, behavior: 'instant' });
        const prevWrap = this.querySelector('.fr-load-prev');
        const newPrev = doc.querySelector('.fr-load-prev');
        if (prevWrap) {
          if (newPrev) prevWrap.innerHTML = newPrev.innerHTML;
          else prevWrap.remove();
        }
      }

      this.layoutPromos();
      if (keyboard && imported[0]) imported[0].querySelector('a[href]')?.focus();
      this.#emit('fr:results:loaded-more', { page, added: imported.length, direction });
    } catch (error) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.querySelector('[data-fr-retry]')?.setAttribute('data-href', pageUrl.toString());
      }
      this.#emit('fr:results:error', { url: pageUrl.toString(), error: String(error), kind: 'load-more' });
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  /* ---------------- hide / show filters ---------------- */

  #syncToggle() {
    const hidden = this.hasAttribute('data-filters-hidden');
    this.querySelectorAll('[data-fr-filter-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(!hidden)));
  }

  toggleFilters(force) {
    const hidden = typeof force === 'boolean' ? force : !this.hasAttribute('data-filters-hidden');
    this.toggleAttribute('data-filters-hidden', hidden);
    safeSession((s) => s.setItem(STORAGE_KEY, hidden ? '1' : '0'));
    this.#syncToggle();
    this.layoutPromos();
    this.#emit('fr:filters:toggle', { hidden });
  }

  /* ---------------- drawer (< 990 px) ---------------- */

  openDrawer(mode = 'filters', opener = null) {
    const drawer = this.filters;
    if (!drawer) return;
    this.#lastFocus = opener || document.activeElement;
    drawer.setAttribute('data-open', '');
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    const title = drawer.querySelector('.fr-filters__title');
    if (title?.id) drawer.setAttribute('aria-labelledby', title.id);
    document.documentElement.classList.add('fr-scroll-lock');
    this.querySelectorAll('[data-fr-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));

    if (mode === 'sort') {
      const sort = drawer.querySelector('[data-fr-sort-group]');
      sort?.scrollIntoView({ block: 'start' });
      (sort?.querySelector('input:checked') || sort?.querySelector('input'))?.focus({ preventScroll: true });
    } else {
      drawer.querySelector('[data-fr-drawer-close]')?.focus();
    }
    this.#emit('fr:drawer:open', { mode });
  }

  closeDrawer() {
    const drawer = this.filters;
    if (!drawer || !drawer.hasAttribute('data-open')) return;
    drawer.removeAttribute('data-open');
    drawer.removeAttribute('role');
    drawer.removeAttribute('aria-modal');
    document.documentElement.classList.remove('fr-scroll-lock');
    this.querySelectorAll('[data-fr-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (this.#lastFocus instanceof HTMLElement && this.#lastFocus.isConnected) this.#lastFocus.focus({ preventScroll: true });
    this.#emit('fr:drawer:close');
  }

  #scrollToResults() {
    const main = this.querySelector('.fr-results__main');
    if (!main) return;
    const offset = parseFloat(getComputedStyle(this).getPropertyValue('--fr-sticky-offset')) || 0;
    const top = main.getBoundingClientRect().top + window.scrollY - offset - 72;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    main.focus({ preventScroll: true });
  }

  /* ---------------- design swatches: search + show more ---------------- */

  #applyDesignSearch(container) {
    if (!container) return;
    const input = container.querySelector('[data-fr-design-search]');
    const q = (input?.value || '').trim().toLowerCase();
    const more = container.querySelector('[data-fr-show-more]');
    const expanded = more?.getAttribute('aria-expanded') === 'true';
    let visible = 0;
    container.querySelectorAll('[data-fr-label]').forEach((li) => {
      const match = !q || (li.getAttribute('data-fr-label') || '').includes(q);
      const overflowHidden = !q && li.hasAttribute('data-fr-overflow') && !expanded;
      li.hidden = !match || overflowHidden;
      if (!li.hidden) visible += 1;
    });
    if (more) more.hidden = Boolean(q);
    const none = container.querySelector('[data-fr-design-nomatch]');
    if (none) none.hidden = visible > 0;
  }

  #toggleShowMore(button, force) {
    const expanded = typeof force === 'boolean' ? force : button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? button.dataset.labelLess || '' : button.dataset.labelMore || '';
    this.#applyDesignSearch(button.closest('.fr-filter__content'));
  }

  /* ---------------- promo tiles ---------------- */

  #observeGrid() {
    this.#ro?.disconnect();
    const grid = this.grid;
    if (!grid || !('ResizeObserver' in window)) return;
    let lastWidth = 0;
    this.#ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width || 0;
      if (Math.abs(w - lastWidth) < 1) return;
      lastWidth = w;
      this.layoutPromos();
    });
    this.#ro.observe(grid);
  }

  layoutPromos() {
    cancelAnimationFrame(this.#promoFrame);
    this.#promoFrame = requestAnimationFrame(() => {
      const grid = this.grid;
      if (!grid) return;
      const promos = [...grid.querySelectorAll(':scope > [data-fr-promo]')];
      if (!promos.length) return;
      const template = getComputedStyle(grid).gridTemplateColumns;
      const cols = Math.max(1, template.split(' ').filter(Boolean).length);
      const cards = grid.querySelectorAll(':scope > [data-fr-card]').length;
      const used = new Map();

      for (const promo of promos) {
        // data-after-cards (sets row): "after N products" whatever the column count, e.g. 20 = row 4 of 5 / row 10 of 2
        const afterCards = Number(promo.dataset.afterCards) || 0;
        const afterRow = afterCards ? Math.ceil(afterCards / cols) : Number(promo.dataset.afterRow) || 2;
        const span = Math.min(Number(promo.dataset.span) || 2, cols);
        const short = cards < (afterCards || afterRow * cols);
        promo.removeAttribute('data-fr-promo-hidden');

        if (short && promo.dataset.ifShort === 'hide') {
          promo.setAttribute('data-fr-promo-hidden', '');
          continue;
        }
        if (short) {
          promo.style.gridRow = 'auto';
          promo.style.gridColumn = `span ${span}`;
          continue;
        }
        let row = afterRow + 1;
        while ((used.get(row) || 0) + span > cols) row += 1;
        used.set(row, (used.get(row) || 0) + span);
        promo.style.gridRow = String(row);
        promo.style.gridColumn = `span ${span}`;
      }
    });
  }
}

if (!customElements.get('fr-results')) customElements.define('fr-results', FrResults);
